import { useState } from "react";
import { X, Loader2 } from "lucide-react";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { useQueryClient } from "@tanstack/react-query";
import { useCreateDevIssue } from "@/features/dev_issues/hooks/useDevIssues";
import { devIssuesApi } from "@/features/dev_issues/api/devIssues";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function ReportIssueModal({ open, onClose }: Props) {
  const { mutateAsync: createIssue, isPending: creating } = useCreateDevIssue();
  const qc = useQueryClient();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit() {
    if (!title.trim()) { setError("Tytuł jest wymagany."); return; }
    if (!description.trim()) { setError("Opis jest wymagany."); return; }
    setError("");
    try {
      const issue = await createIssue({ title: title.trim(), description: description.trim() });
      if (files.length > 0) {
        setUploading(true);
        for (const file of files) {
          await devIssuesApi.uploadAttachment(issue.id, file);
        }
        await qc.invalidateQueries({ queryKey: ["dev-issues", issue.id] });
      }
      handleClose();
    } catch {
      setError("Wystąpił błąd podczas zgłaszania. Spróbuj ponownie.");
    } finally {
      setUploading(false);
    }
  }

  function handleClose() {
    setTitle("");
    setDescription("");
    setFiles([]);
    setError("");
    onClose();
  }

  if (!open) return null;

  const busy = creating || uploading;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-3xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Zgłoś problem techniczny</h2>
          <button
            onClick={handleClose}
            disabled={busy}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 disabled:opacity-40"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-col gap-4 px-6 py-5 overflow-y-auto flex-1">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Tytuł <span className="text-red-500">*</span>
            </label>
            <input
              autoFocus
              value={title}
              onChange={(e) => { setTitle(e.target.value); setError(""); }}
              onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="Krótki opis problemu…"
              maxLength={200}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Opis <span className="text-red-500">*</span>
            </label>
            <RichTextEditor
              value={description}
              onChange={(val) => { setDescription(val); setError(""); }}
              disabled={busy}
              placeholder="Opisz dokładnie co się dzieje, kiedy i jak to odtworzyć…"
              onImageUpload={(file) => devIssuesApi.uploadDescriptionImage(file)}
              initialHeight={280}
            />
          </div>

          {/* Attachment drop zone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Zrzuty ekranu / pliki <span className="text-gray-400 dark:text-gray-500 font-normal">(opcjonalnie)</span>
            </label>
            <FileDropZone
              files={files}
              onFilesChange={setFiles}
              multiple
              accept="image/*,application/pdf"
            />
          </div>

          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex-shrink-0">
          <button
            onClick={handleClose}
            disabled={busy}
            className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 disabled:opacity-40"
          >
            Anuluj
          </button>
          <button
            onClick={handleSubmit}
            disabled={busy || !title.trim() || !description.trim()}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--color-primary)] text-white text-sm font-medium rounded-lg hover:bg-[var(--color-primary-hover)] disabled:opacity-40 transition-colors"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            Zgłoś problem
          </button>
        </div>
      </div>
    </div>
  );
}
